import { supabase as feedbackSupabase } from '../lib/supabase';
import type { SupabaseClient } from '@supabase/supabase-js';

export interface Idea {
  id: string;
  created_at: string;
  updated_at: string;
  title: string;
  description: string | null;
  original_link: string | null;
  thumbnail_url: string | null;
  video_file_url: string | null;
  funnel_level: 'tofu' | 'mofu' | 'bofu' | null;
  content_type: 'song_specific' | 'off_topic' | null;
  video_submission_id: string | null;
  content_plan_event_id: string | null;
  status: 'unused' | 'used' | 'rejected';
  artist_id: string;
  analysis_raw: any | null;
}

export class IdeasService {
  static async getIdeas(artistId?: string, client?: SupabaseClient<any>): Promise<Idea[]> {
    const supabase = (client || feedbackSupabase) as any;
    let query = supabase
      .from('ideas')
      .select('*')
      .order('created_at', { ascending: false });

    if (artistId) {
      query = query.eq('artist_id', artistId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching ideas:', error);
      throw error;
    }

    return (data || []) as Idea[];
  }

  static async getIdea(id: string, client?: SupabaseClient<any>): Promise<Idea | null> {
    const supabase = (client || feedbackSupabase) as any;
    const { data, error } = await supabase
      .from('ideas')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching idea:', error);
      throw error;
    }

    return data as Idea | null;
  }

  static async createIdea(idea: Omit<Idea, 'id' | 'created_at' | 'updated_at'>, client?: SupabaseClient<any>): Promise<Idea> {
    const supabase = (client || feedbackSupabase) as any;
    const { data, error } = await supabase
      .from('ideas')
      .insert(idea)
      .select()
      .single();

    if (error) {
      console.error('Error creating idea:', error);
      throw error;
    }

    return data as Idea;
  }

  static async updateIdea(id: string, updates: Partial<Idea>, client?: SupabaseClient<any>): Promise<Idea> {
    const supabase = (client || feedbackSupabase) as any;
    const { data, error } = await supabase
      .from('ideas')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating idea:', error);
      throw error;
    }

    return data as Idea;
  }

  static async deleteIdea(id: string, client?: SupabaseClient<any>): Promise<void> {
    const supabase = (client || feedbackSupabase) as any;
    const { error } = await supabase
      .from('ideas')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting idea:', error);
      throw error;
    }
  }

  static async linkToSubmission(ideaId: string, submissionId: string, client?: SupabaseClient<any>): Promise<Idea> {
    return this.updateIdea(ideaId, { video_submission_id: submissionId, status: 'used' }, client);
  }

  static async linkToContentPlanEvent(ideaId: string, contentPlanEventId: string, client?: SupabaseClient<any>): Promise<Idea> {
    return this.updateIdea(ideaId, { content_plan_event_id: contentPlanEventId }, client);
  }
}
